import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, ArrowLeft, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PedigreeTree from "@/components/PedigreeTree";
import { useI18n } from "@/context/I18nContext";

const MAX_GENERATIONS = 3;

function findByRing(birds, ring) {
  if (!ring) return null;
  const r = ring.trim();
  if (!r) return null;
  return birds.find((b) => (b.ring || "").trim() === r) || null;
}

function buildNode(birds, ring, mutationFallback, depth) {
  if (!ring) return null;
  const known = findByRing(birds, ring);
  const node = {
    ring,
    sex: known?.sex || "",
    mutation: known?.mutation || mutationFallback || "",
    photo_id: known?.photo_id || "",
    father: null,
    mother: null,
  };
  if (known && depth < MAX_GENERATIONS) {
    node.father = buildNode(birds, known.father_ring, known.father_mutation, depth + 1);
    node.mother = buildNode(birds, known.mother_ring, known.mother_mutation, depth + 1);
  }
  return node;
}

export default function PedigreePage() {
  const { ring: ringParam } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [birds, setBirds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(ringParam || "");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/schedule/birds");
      setBirds(data.birds || []);
    } catch {/* pedigree page only needs birds for ring lookups; missing is acceptable */}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (ringParam) setSearch(ringParam);
  }, [ringParam]);

  const tree = useMemo(() => {
    if (!search) return null;
    return buildNode(birds, search, "", 0);
  }, [search, birds]);

  const onGo = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/pedigree/${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="space-y-6" data-testid="pedigree-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-xs uppercase tracking-widest text-espresso/60 hover:text-olive flex items-center gap-1 mb-1"
            data-testid="pedigree-back"
          >
            <ArrowLeft className="w-3 h-3" /> {t("common.back")}
          </button>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
            <GitBranch className="w-7 h-7 text-olive" /> {t("pedigree.title")}
          </h1>
          <p className="text-sm text-espresso/70 mt-1">
            {t("pedigree.sub")}
          </p>
        </div>
        <form onSubmit={onGo} className="flex items-center gap-2" data-testid="pedigree-search-form">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("pedigree.placeholder")}
            className="font-mono w-56"
            data-testid="pedigree-search-input"
          />
          <Button
            type="submit"
            className="bg-olive hover:bg-olive-hover text-white"
            data-testid="pedigree-search-submit"
          >
            {t("pedigree.show")}
          </Button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-app-border p-4 sm:p-6 shadow-sm overflow-x-auto scrollbar-thin">
        {loading ? (
          <div className="grid place-items-center py-12 text-espresso/60">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : !search ? (
          <div className="text-center py-12 text-espresso/60">
            {t("pedigree.enter")}
            <div className="mt-2 text-xs">
              <Link to="/birds" className="text-olive font-semibold hover:underline">
                {t("nav.birds")}
              </Link>
            </div>
          </div>
        ) : !tree ? (
          <div className="text-center py-12 text-espresso/60" data-testid="pedigree-empty-state">
            {t("pedigree.notfound")} "{search}".
          </div>
        ) : (
          <PedigreeTree root={tree} />
        )}
      </div>
    </div>
  );
}
